/* ================================================================
   TCB GIFT CARD PDP — JavaScript
   Handles: amount selection, delivery method, send timing,
            gift message, form validation, AJAX cart submission.
   Scoped entirely to the gift card PDP — does NOT touch any
   existing PDP functions or global state.
================================================================ */

(function () {
  'use strict';

  /* ── Currency config (set by the section before this script) ──
     cart.currency = the shopper's live presentment currency, per country.
     All variant data-price values are already in this currency (cents),
     converted by Shopify Markets — so what we display is exactly what
     /cart/add.js charges at checkout. */
  const CFG = window.TCB_GC || {};
  const CURRENCY      = CFG.currency     || 'USD';
  const CURRENCY_SYM  = CFG.currencySym  || '$';
  const SHOP_CURRENCY = CFG.shopCurrency || 'USD';
  const LOCALE        = CFG.locale       || 'en';
  // Currencies with no minor unit — affects input parsing / display only.
  const ZERO_DECIMAL  = ['JPY', 'KRW', 'VND', 'CLP', 'ISK', 'KMF', 'DJF', 'GNF', 'PYG', 'RWF', 'UGX', 'VUV', 'XAF', 'XOF', 'XPF', 'BIF'];

  /* ── State ─────────────────────────────────────────────────── */
  const GC = {
    mode:           'preset', // 'preset' | 'custom'
    customCents:    0,        // shopper's raw custom entry, local cents
    variants:       [],       // all denominations: {id, price(local cents), usd, available}
    variantId:      null,
    variantPrice:   0,
    design:         '',       // selected "Design" option value ('' when the product has no Design option)
    deliveryMethod: 'email', // digital only
    sendTiming:     'now',  // 'now' | 'scheduled'
    schedDate:      '',
    schedTime:      '',
    recipEmail:     '',
    recipName:      '',
    recipPhone:     '',
    message:        '',
    shipName:       '',
    shipAddr1:      '',
    shipAddr2:      '',
    shipCity:       '',
    shipState:      '',
    shipZip:        '',
    shipCountry:    'US',
    addons:         {},     // key → price in cents
  };

  /* ── Design tiles ───────────────────────────────────────────────
     Each design x amount is a real variant. Every amount pill carries
     data-design; choosing a design shows only that design's pills and
     re-selects the same amount, so GC.variantId always points at the
     variant for (design, amount). No Design option → no tiles → no-op. */
  function gcAmountPills() {
    return Array.prototype.slice.call(
      document.querySelectorAll('.gc-amount-pill:not(.gc-amount-pill--custom)')
    );
  }

  function gcApplyDesign(tile, isInit) {
    const design = tile.dataset.design || '';
    GC.design = design;

    document.querySelectorAll('.gc-design-tile').forEach(function (t) {
      const on = t === tile;
      t.classList.toggle('active', on);
      t.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    const pills   = gcAmountPills();
    const current = document.querySelector('.gc-amount-pill.active:not(.gc-amount-pill--custom)');
    const usd     = current ? current.dataset.usd : null;
    pills.forEach(function (p) { p.hidden = (p.dataset.design || '') !== design; });

    if (isInit) return; // gcInitAmounts() auto-selects the first visible pill

    const visible = pills.filter(function (p) {
      return !p.hidden && !p.classList.contains('gc-amount-pill--soldout');
    });
    const target = visible.filter(function (p) { return p.dataset.usd === usd; })[0] || visible[0];
    if (target) {
      target.click();
    } else {
      pills.forEach(function (p) { p.classList.remove('active'); });
      GC.variantId = null;
      GC.variantPrice = 0;
      gcUpdatePriceDisplay();
    }

    // Show the chosen design in the gallery + sticky bar
    const idx    = parseInt(tile.dataset.imageIndex, 10);
    const thumbs = document.querySelectorAll('.pdp-thumbs .pdp-thumb');
    if (idx >= 0 && thumbs[idx] && typeof window.pdpThumbClick === 'function') {
      window.pdpThumbClick(thumbs[idx], tile.dataset.image, idx);
    } else if (tile.dataset.image) {
      const mainImg = document.getElementById('pdpMainImage');
      if (mainImg) { mainImg.removeAttribute('srcset'); mainImg.src = tile.dataset.image; }
    }
    const stickyThumb = document.querySelector('#gcStickyBar .pdp-sticky-thumb');
    if (stickyThumb && tile.dataset.thumb) stickyThumb.src = tile.dataset.thumb;
  }

  function gcInitDesigns() {
    const tiles = document.querySelectorAll('.gc-design-tile');
    if (!tiles.length) return;
    tiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        if (tile.classList.contains('active')) return;
        gcApplyDesign(tile, false);
      });
    });
    gcApplyDesign(document.querySelector('.gc-design-tile.active') || tiles[0], true);
  }

  /* ── Amount / Variant Pills ─────────────────────────────────── */
  function gcInitAmounts() {
    const pills = document.querySelectorAll('.gc-amount-pill:not(.gc-amount-pill--custom)');
    if (!pills.length) return;

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        document.querySelectorAll('.gc-amount-pill').forEach(function (p) { p.classList.remove('active'); });
        pill.classList.add('active');

        GC.mode         = 'preset';
        GC.variantId    = pill.dataset.variantId;
        GC.variantPrice = parseInt(pill.dataset.price, 10) || 0;

        gcCloseCustomPanel();
        gcUpdatePriceDisplay();
        gcClearError('gc-amount-error');
      });
    });

    // Auto-select first available variant
    const first = document.querySelector('.gc-amount-pill:not(.gc-amount-pill--soldout):not(.gc-amount-pill--custom):not([hidden])');
    if (first) first.click();
  }

  function gcSelectedCents() {
    // In both modes the charge is the selected variant's live local price —
    // exactly what /cart/add.js will charge at checkout.
    return GC.variantPrice;
  }

  function gcUpdatePriceDisplay() {
    const cents = gcSelectedCents();
    if (cents <= 0) return;
    const formatted = gcFormatMoney(cents);
    const el  = document.getElementById('gcPriceDisplay');
    const lbl = document.getElementById('gcOrderPriceLabel');
    const sticky = document.getElementById('gcStickyPrice');
    if (el)     el.textContent     = formatted;
    if (lbl)    lbl.textContent    = formatted;
    if (sticky) sticky.textContent = formatted;
  }

  /* Format cents in the shopper's own currency + locale (was hardcoded $/en-US). */
  function gcFormatMoney(cents, currencyOverride) {
    const curr = currencyOverride || CURRENCY;
    const amount = cents / 100;
    try {
      const wholeNumber = amount === Math.floor(amount);
      return new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: curr,
        minimumFractionDigits: wholeNumber ? 0 : undefined,
      }).format(amount);
    } catch (e) {
      // Ancient-browser fallback — symbol + grouped number
      return CURRENCY_SYM + amount.toFixed(amount === Math.floor(amount) ? 0 : 2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }

  /* ── Custom Amount (theme-only) ─────────────────────────────────
     The shopper types an amount in THEIR currency; we match it to the
     closest denomination on the gift card product (every variant is in
     the JSON blob the section renders). The matched variant's live
     Markets-converted price is shown before Add To Bag, and that exact
     figure is what /cart/add.js charges at checkout — the normal cart
     flow, no app, no backend. Add more denominations to the product in
     Shopify admin (e.g. $100 steps) for finer matching. */
  function gcInitCustomAmount() {
    const pill  = document.getElementById('gcCustomPill');
    const panel = document.getElementById('gcCustomPanel');
    const input = document.getElementById('gcCustomInput');
    const data  = document.getElementById('gcVariantData');
    if (!pill || !panel || !input || !data) {
      if (pill) pill.style.display = 'none';
      return;
    }

    try {
      GC.variants = JSON.parse(data.textContent).filter(function (v) {
        return v.available && v.price > 0;
      });
    } catch (e) { GC.variants = []; }

    if (!GC.variants.length) {
      pill.style.display = 'none';
      return;
    }

    pill.addEventListener('click', function () {
      document.querySelectorAll('.gc-amount-pill').forEach(function (p) { p.classList.remove('active'); });
      pill.classList.add('active');
      pill.setAttribute('aria-expanded', 'true');
      GC.mode = 'custom';
      GC.variantId = null;
      GC.variantPrice = 0;
      panel.classList.add('open');
      gcClearError('gc-amount-error');
      gcParseCustomInput(); // refresh match with whatever is typed
      setTimeout(function () { input.focus(); }, 120);
    });

    input.addEventListener('input', gcParseCustomInput);
  }

  function gcCloseCustomPanel() {
    const panel = document.getElementById('gcCustomPanel');
    const pill  = document.getElementById('gcCustomPill');
    if (panel) panel.classList.remove('open');
    if (pill)  pill.setAttribute('aria-expanded', 'false');
  }

  function gcCustomBounds() {
    // Everything between the smallest and largest live denomination is fair game.
    let min = Infinity, max = 0;
    GC.variants.forEach(function (v) {
      if (v.price < min) min = v.price;
      if (v.price > max) max = v.price;
    });
    return { min: min, max: max };
  }

  function gcParseCustomInput() {
    const input = document.getElementById('gcCustomInput');
    const hint  = document.getElementById('gcCustomHint');
    if (!input) return;

    // Accept "1500", "1,500", "1.500" (EU thousands), "1500.50", "1 500,50".
    // Rule: the LAST separator is a decimal point only if 1–2 digits follow
    // it; 3+ digits after it means it's a thousands grouper.
    const rawStr  = (input.value || '').replace(/[^\d.,]/g, '');
    const lastSep = Math.max(rawStr.lastIndexOf('.'), rawStr.lastIndexOf(','));
    let intPart   = rawStr;
    let decPart   = '';
    if (lastSep !== -1) {
      const after = rawStr.slice(lastSep + 1);
      if (after.length >= 1 && after.length <= 2) {
        decPart = after;
        intPart = rawStr.slice(0, lastSep);
      }
    }
    intPart = intPart.replace(/[.,]/g, '');
    const raw = intPart + (decPart ? '.' + decPart : '');
    const val = parseFloat(raw);

    if (!raw || isNaN(val) || val <= 0) {
      GC.customCents = 0;
      GC.variantId = null;
      GC.variantPrice = 0;
      if (hint) { hint.textContent = ''; hint.classList.remove('gc-custom-hint--ok'); }
      gcClearError('gc-custom-error');
      return;
    }

    const isZeroDec = ZERO_DECIMAL.indexOf(CURRENCY) !== -1;
    const cents  = isZeroDec ? Math.round(val) * 100 : Math.round(val * 100);
    const bounds = gcCustomBounds();

    // Allow a little headroom below/above so "990" still matches "1,000"
    if (cents < bounds.min * 0.5 || cents > bounds.max * 1.05) {
      GC.customCents = 0;
      GC.variantId = null;
      GC.variantPrice = 0;
      if (hint) { hint.textContent = ''; hint.classList.remove('gc-custom-hint--ok'); }
      gcShowError('gc-custom-error',
        'Gift cards are available from ' + gcFormatMoney(bounds.min) + ' to ' + gcFormatMoney(bounds.max) + '.');
      return;
    }

    gcClearError('gc-custom-error');
    GC.customCents = cents;

    // ── Match the closest denomination (by live local price) ──────
    let best = null, bestDist = Infinity;
    GC.variants.forEach(function (v) {
      const d = Math.abs(v.price - cents);
      if (d < bestDist || (d === bestDist && best && v.price < best.price)) {
        best = v; bestDist = d;
      }
    });
    if (!best) return;

    GC.variantId    = String(best.id);
    GC.variantPrice = best.price;

    if (hint) {
      const exact = bestDist === 0;
      let txt;
      if (exact) {
        txt = 'You’ll pay exactly ' + gcFormatMoney(best.price) + ' at checkout.';
      } else {
        txt = 'Closest available value: ' + gcFormatMoney(best.price)
            + ' — this exact amount is charged at checkout.';
      }
      if (CURRENCY !== SHOP_CURRENCY && best.usd > 0) {
        txt += ' (a $' + Number(best.usd).toLocaleString('en-US') + ' USD gift card)';
      }
      hint.textContent = txt;
      hint.classList.add('gc-custom-hint--ok');
    }

    gcUpdatePriceDisplay();
  }

  /* ── Delivery Method Cards ──────────────────────────────────── */
  function gcInitDelivery() {
    const cards = document.querySelectorAll('.gc-del-card');
    if (!cards.length) return;

    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        const method = card.dataset.method;
        gcSelectDelivery(method);
      });
    });
  }

  function gcSelectDelivery(method) {
    GC.deliveryMethod = method;

    // Update card active states
    document.querySelectorAll('.gc-del-card').forEach(function (c) {
      c.classList.toggle('active', c.dataset.method === method);
    });

    // Show/hide recipient forms
    const emailForm    = document.getElementById('gcEmailForm');
    const physicalForm = document.getElementById('gcPhysicalForm');

    if (method === 'email') {
      gcOpenPanel(emailForm);
      gcClosePanel(physicalForm);
      // Release the max-height lock after the open animation so the schedule
      // date/time fields can expand freely inside the form without being clipped.
      setTimeout(function () {
        if (emailForm) {
          emailForm.style.maxHeight = 'none';
          emailForm.style.overflow  = 'visible';
        }
      }, 500);
    } else {
      gcClosePanel(emailForm);
      gcOpenPanel(physicalForm);
      setTimeout(function () {
        if (physicalForm) {
          physicalForm.style.maxHeight = 'none';
          physicalForm.style.overflow  = 'visible';
        }
      }, 500);
    }

    gcClearError('gc-delivery-error');
  }

  function gcOpenPanel(el) {
    if (!el) return;
    el.classList.add('open');
    // Measure at full height — use scrollHeight of inner child if available
    var inner = el.firstElementChild;
    var h = inner ? inner.scrollHeight : el.scrollHeight;
    el.style.maxHeight = (h + 60) + 'px'; // +60px safety margin for padding
    el.style.transition = 'max-height .45s cubic-bezier(.16,1,.3,1), opacity .3s ease';
    // Re-measure after children may have rerendered
    setTimeout(function () {
      var inner2 = el.firstElementChild;
      var h2 = inner2 ? inner2.scrollHeight : el.scrollHeight;
      el.style.maxHeight = (h2 + 60) + 'px';
    }, 80);
  }

  function gcClosePanel(el) {
    if (!el) return;
    el.classList.remove('open');
    el.style.maxHeight = '0';
    el.style.transition = 'max-height .35s cubic-bezier(.4,0,.2,1), opacity .25s ease';
  }

  /* ── Send Timing (Email delivery only) ─────────────────────── */
  function gcInitSendTiming() {
    const opts = document.querySelectorAll('.gc-send-opt');
    if (!opts.length) return;

    opts.forEach(function (opt) {
      opt.addEventListener('click', function () {
        const timing = opt.dataset.timing;
        GC.sendTiming = timing;
        opts.forEach(function (o) { o.classList.remove('active'); });
        opt.classList.add('active');

        const schedWrap = document.getElementById('gcSchedWrap');
        if (schedWrap) {
          const isScheduled = timing === 'scheduled';
          schedWrap.classList.toggle('open', isScheduled);
          if (isScheduled) {
            // Release overflow after the 450ms max-height animation completes so
            // desktop browser date/time picker popups are not clipped by overflow:hidden.
            setTimeout(function () {
              schedWrap.style.overflow = 'visible';
            }, 500);
          } else {
            // Restore hidden overflow so the collapse animation works next time.
            schedWrap.style.overflow = '';
          }
        }
      });
    });
  }

  /* ── Gift Message Toggle ────────────────────────────────────── */
  function gcInitGiftMessage() {
    var area    = document.getElementById('gcMsgText');
    var counter = document.getElementById('gcMsgCounter');

    if (!area) return;

    var MAX = 200; // Shopify's native gift card recipient message limit
    area.addEventListener('input', function () {
      GC.message = area.value;
      if (counter) {
        counter.textContent = (MAX - area.value.length) + ' characters remaining';
      }
    });
  }

  /* ── Elevate the Gift — Add-ons ────────────────────────────── */
  function gcToggleAddon(el, key, priceInCents) {
    el.classList.toggle('active');
    const isActive = el.classList.contains('active');
    el.setAttribute('aria-checked', isActive ? 'true' : 'false');
    if (isActive) {
      GC.addons[key] = priceInCents;
    } else {
      delete GC.addons[key];
    }
  }

  /* ── Accordions (deep-dive sections) ───────────────────────── */
  function gcInitAccordions() {
    document.querySelectorAll('.gc-acc-header').forEach(function (header) {
      header.addEventListener('click', function () {
        const section = header.closest('.gc-acc-section');
        if (!section) return;
        const body = section.querySelector('.gc-acc-body');
        const icon = header.querySelector('.gc-acc-icon');
        const isOpen = section.classList.contains('open');

        if (isOpen) {
          section.classList.remove('open');
          gcClosePanel(body);
          if (icon) icon.textContent = '+';
        } else {
          section.classList.add('open');
          gcOpenPanel(body);
          if (icon) icon.textContent = '−';
        }
      });
    });
  }

  /* ── Form Field Sync ────────────────────────────────────────── */
  function gcInitFieldSync() {
    const fieldMap = {
      'gcRecipEmail':   function (v) { GC.recipEmail   = v; },
      'gcRecipName':    function (v) { GC.recipName    = v; },
      'gcRecipPhone':   function (v) { GC.recipPhone   = v; },
      'gcShipPhone':    function (v) { GC.recipPhone   = v; }, // physical form phone
      'gcSchedDate':    function (v) { GC.schedDate    = v; },
      'gcSchedTime':    function (v) { GC.schedTime    = v; },
      'gcShipName':     function (v) { GC.shipName     = v; },
      'gcShipAddr1':    function (v) { GC.shipAddr1    = v; },
      'gcShipAddr2':    function (v) { GC.shipAddr2    = v; },
      'gcShipCity':     function (v) { GC.shipCity     = v; },
      'gcShipState':    function (v) { GC.shipState    = v; },
      'gcShipZip':      function (v) { GC.shipZip      = v; },
      'gcShipCountry':  function (v) { GC.shipCountry  = v; },
    };

    Object.keys(fieldMap).forEach(function (id) {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('input', function () { fieldMap[id](el.value.trim()); });
        el.addEventListener('change', function () { fieldMap[id](el.value.trim()); });
      }
    });
  }

  /* ── Validation ─────────────────────────────────────────────── */
  function gcValidate() {
    let valid = true;
    gcSyncFromDom();
    var _vd = document.getElementById('gcSchedDate');
    if (_vd) GC.schedDate = _vd.value || '';

    // Must have an amount — a preset pill, or a matched custom amount
    if (GC.mode === 'custom') {
      if (!GC.variantId) {
        const bounds = gcCustomBounds();
        gcShowError('gc-custom-error',
          'Please enter an amount between ' + gcFormatMoney(bounds.min) + ' and ' + gcFormatMoney(bounds.max) + '.');
        valid = false;
      }
    } else if (!GC.variantId) {
      gcShowError('gc-amount-error', 'Please select a gift card amount.');
      valid = false;
    }

    if (!GC.recipEmail || !gcValidEmail(GC.recipEmail)) {
      gcShowError('gc-email-error', 'Please enter a valid recipient email address.');
      valid = false;
    }
    if (!GC.recipName) {
      gcShowError('gc-name-error', 'Please enter the recipient\'s name.');
      valid = false;
    }
    if (GC.sendTiming === 'scheduled') {
      if (!GC.schedDate) {
        gcShowError('gc-sched-error', 'Please select a delivery date.');
        valid = false;
      } else {
        // Shopify accepts a send date from today up to 90 days ahead; anything
        // else is rejected by the cart, so catch it here with a clear message.
        var _max = new Date(); _max.setDate(_max.getDate() + 90);
        if (GC.schedDate < gcLocalYmd(new Date()) || GC.schedDate > gcLocalYmd(_max)) {
          gcShowError('gc-sched-error', 'Please choose a date between today and 90 days from now.');
          valid = false;
        }
      }
    }

    return valid;
  }

  function gcValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function gcShowError(id, msg) {
    const el = document.getElementById(id);
    if (el) { el.textContent = msg; el.style.display = 'block'; }
  }

  function gcClearError(id) {
    const el = document.getElementById(id);
    if (el) { el.textContent = ''; el.style.display = 'none'; }
  }

  function gcClearAllErrors() {
    document.querySelectorAll('.gc-field-error').forEach(function (el) {
      el.textContent = '';
      el.style.display = 'none';
    });
  }

  /* ── Build Line Item Properties ─────────────────────────────── */
  function gcBuildProperties() {
    const props = {};

    // Re-read date/time directly from DOM at submission time.
    // iOS and desktop browsers fire picker change events at different moments;
    // reading the live .value here guarantees we never miss a selection.
    var _dateEl = document.getElementById('gcSchedDate');
    var _timeEl = document.getElementById('gcSchedTime');
    if (_dateEl && _dateEl.value) GC.schedDate = _dateEl.value;
    if (_timeEl && _timeEl.value) GC.schedTime = _timeEl.value;
    // Same for the recipient fields + message — autofill and paste do not
    // always fire an input event, so read the live values at submission.
    gcSyncFromDom();

    // Digital email delivery only
    props['__shopify_send_gift_card_to_recipient'] = 'if_present';
    props['Recipient email'] = GC.recipEmail;
    props['Recipient name']  = GC.recipName;
    if (GC.message) {
      props['Message'] = GC.message;
    }
    if (GC.sendTiming === 'scheduled' && GC.schedDate) {
      // Shopify validates 'Send on' as a DATE only (YYYY-MM-DD).
      // Including a time component (T00:14:00) causes a 422 "must be a valid date" error.
      // Time is stored in a separate property so it appears in cart, checkout, and order confirmation.
      props['Send on'] = GC.schedDate;
      if (GC.schedTime) {
        props['Delivery time'] = gcFormatTime(GC.schedTime) + gcTimeZoneLabel();
      }
    }
    if (GC.recipPhone) {
      props['Recipient phone'] = GC.recipPhone;
    }
    props['Delivery method'] = 'Digital — Email';

    // Gift card face value label (cosmetic, for cart display) — shown in the
    // shopper's own currency, with the ISO code appended when it isn't the
    // store currency so the order admin is unambiguous.
    var _gcValCents = gcSelectedCents();
    props['Gift card value'] = gcFormatMoney(_gcValCents)
      + (CURRENCY !== SHOP_CURRENCY ? ' ' + CURRENCY : '');

    // Add-ons (Elevate the Gift)
    var addonKeys = Object.keys(GC.addons);
    if (addonKeys.length) {
      var addonLabels = addonKeys.map(function (k) {
        if (k === 'letterpress-note')  return 'Letterpress Keepsake Note (+$35)';
        if (k === 'concierge-intro')   return 'Concierge Introduction';
        return k;
      });
      props['Add-ons'] = addonLabels.join(', ');
    }

    return props;
  }

  function gcSyncFromDom() {
    var map = { gcRecipEmail: 'recipEmail', gcRecipName: 'recipName', gcRecipPhone: 'recipPhone', gcMsgText: 'message' };
    Object.keys(map).forEach(function (id) {
      var el = document.getElementById(id);
      if (el) GC[map[id]] = (el.value || '').trim();
    });
  }

  /* The shopper's own time zone, so the fulfilment team knows which
     "3:00 PM" was meant — e.g. " (America/New_York)". */
  function gcTimeZoneLabel() {
    try {
      var tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz ? ' (' + tz + ')' : '';
    } catch (e) { return ''; }
  }

  function gcLocalYmd(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  function gcFormatTime(t) {
    if (!t) return '';
    const parts = t.split(':');
    let h = parseInt(parts[0], 10);
    const m = parts[1] || '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return h + ':' + m + ' ' + ampm;
  }

  /* ── AJAX Cart Submission ───────────────────────────────────── */
  function gcSubmit() {
    gcClearAllErrors();

    if (!gcValidate()) {
      // Scroll to first visible error
      const firstErr = document.querySelector('.gc-field-error[style*="block"]');
      if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const btn = document.getElementById('gcAddBtn');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
    }

    const props = gcBuildProperties();

    const formData = {
      id:         parseInt(GC.variantId, 10),
      quantity:   1,
      properties: props,
    };

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body:    JSON.stringify(formData),
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (d) {
          var desc = d.description;
          var msg  = typeof desc === 'string' ? desc : (desc ? JSON.stringify(desc) : (d.message || 'Cart error'));
          throw new Error(msg);
        });
        return res.json();
      })
      .then(function () {
        // Refresh cart drawer data then open — mirrors every other TCB PDP
        // (refreshCartDrawer fetches /cart.js, rebuilds drawer HTML, then fires the callback)
        gcClearError('gc-cart-error');
        if (typeof refreshCartDrawer === 'function' && typeof openCartDrawer === 'function') {
          refreshCartDrawer(function () { openCartDrawer(); });
        } else {
          gcOpenCartDrawer();
        }

        if (btn) {
          btn.disabled    = false;
          btn.textContent = 'Add To Bag';
        }
      })
      .catch(function (err) {
        console.error('[TCB GC] Cart error:', err);
        var msg = (err && typeof err.message === 'string' && err.message)
          ? err.message
          : (typeof err === 'string' ? err : 'Something went wrong. Please try again.');
        gcShowError('gc-cart-error', msg);
        if (btn) {
          btn.disabled    = false;
          btn.textContent = 'Add To Bag';
        }
      });
  }

  function gcOpenCartDrawer() {
    // Try common Dawn/TCB cart drawer event patterns
    const cartEvents = [
      'cart:refresh',
      'cart:open',
      'cart-open',
      'cartDrawer:open',
    ];

    let opened = false;

    cartEvents.forEach(function (evtName) {
      if (!opened) {
        try {
          document.dispatchEvent(new CustomEvent(evtName, { bubbles: true }));
        } catch (e) {}
      }
    });

    // Also try calling the global openCartDrawer function if it exists
    if (typeof window.openCartDrawer === 'function') {
      try { window.openCartDrawer(); opened = true; } catch (e) {}
    }
    if (typeof window.tcbOpenCart === 'function') {
      try { window.tcbOpenCart(); opened = true; } catch (e) {}
    }

    // Fallback: refresh cart count
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const countEls = document.querySelectorAll('.cart-count, [data-cart-count], .cart-item-count');
        countEls.forEach(function (el) {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
      })
      .catch(function () {});
  }

  /* ── Apple Pay ───────────────────────────────────────────────
     Mirrors the working PDP pattern exactly:
     validate → add GC to cart with full properties → /checkout
     Shopify's native checkout page surfaces Apple Pay (Face ID /
     Touch ID) — no ApplePaySession needed here.
  ─────────────────────────────────────────────────────────── */
  function gcInitApplePay() {
    var appleBtn = document.getElementById('gcApplePayBtn');
    var apWrap   = document.getElementById('gcApplePayWrap');
    if (!appleBtn) return;

    // Hide entirely on non-Apple-Pay browsers (Chrome, Firefox, etc.)
    if (!window.ApplePaySession || !ApplePaySession.canMakePayments()) {
      if (apWrap) apWrap.style.display = 'none';
      return;
    }

    appleBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();

      // 1 — Validate all required fields (same rules as Add To Bag)
      gcClearAllErrors();
      if (!gcValidate()) {
        var firstErr = document.querySelector('.gc-field-error[style*="block"]');
        if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // 2 — Build cart properties (same as gcSubmit)
      var props = gcBuildProperties();

      // 3 — Add gift card to cart, then hand off to Shopify checkout
      //     which shows Apple Pay natively (Face ID / Touch ID).
      appleBtn.setAttribute('disabled', '');

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ id: parseInt(GC.variantId, 10), quantity: 1, properties: props }),
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (d) { throw new Error(d.description || 'Cart error'); });
          return res.json();
        })
        .then(function () {
          window.location.href = '/checkout';
        })
        .catch(function (err) {
          console.error('[TCB GC Apple Pay] cart error:', err);
          gcShowError('gc-cart-error', err.message || 'Something went wrong. Please try again.');
          appleBtn.removeAttribute('disabled');
        });
    });
  }

  /* ── Sticky bar price mirror ────────────────────────────────── */
  function gcInitSticky() {
    window.addEventListener('scroll', function () {
      var bar = document.getElementById('gcStickyBar');
      var btn = document.getElementById('gcAddBtn');
      if (btn && bar) {
        var rect = btn.getBoundingClientRect();
        bar.classList.toggle('visible', rect.bottom < 0);
      }
    }, { passive: true });
  }

  /* ── Date min = today ───────────────────────────────────────── */
  function gcInitDateMin() {
    const dateInput = document.getElementById('gcSchedDate');
    if (!dateInput) return;
    const today = new Date();
    const yyyy  = today.getFullYear();
    const mm    = String(today.getMonth() + 1).padStart(2, '0');
    const dd    = String(today.getDate()).padStart(2, '0');
    dateInput.min = yyyy + '-' + mm + '-' + dd;
    var maxD = new Date(); maxD.setDate(maxD.getDate() + 90);
    dateInput.max = gcLocalYmd(maxD);
  }

  /* ── Share button ───────────────────────────────────────────── */
  function gcInitShare() {
    const shareBtn = document.getElementById('gcShareBtn');
    const shareDrop = document.getElementById('gcShareDropdown');
    if (!shareBtn || !shareDrop) return;

    shareBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      shareDrop.classList.toggle('open');
    });

    document.addEventListener('click', function () {
      shareDrop.classList.remove('open');
    });

    // Set share link hrefs
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(document.title);

    const fb = document.getElementById('gcShareFacebook');
    if (fb) fb.href = 'https://www.facebook.com/sharer/sharer.php?u=' + url;

    const tw = document.getElementById('gcShareX');
    if (tw) tw.href = 'https://twitter.com/intent/tweet?url=' + url + '&text=' + title;

    const wa = document.getElementById('gcShareWhatsApp');
    if (wa) wa.href = 'https://api.whatsapp.com/send?text=' + title + '%20' + url;

    const pi = document.getElementById('gcSharePinterest');
    if (pi) pi.href = 'https://pinterest.com/pin/create/button/?url=' + url + '&description=' + title;
  }

  function gcCopyLink(btn) {
    navigator.clipboard.writeText(window.location.href).then(function () {
      const orig = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(function () { btn.textContent = orig; }, 2000);
    });
  }

  /* ── Favorites heart ────────────────────────────────────────── */
  // Reuses global toggleFav() already defined in theme JS

  /* ── Expose public API ──────────────────────────────────────── */
  window.gcSubmit       = gcSubmit;
  window.gcCopyLink     = gcCopyLink;
  window.gcToggleAddon  = gcToggleAddon;

  /* ── Init ───────────────────────────────────────────────────── */
  function gcInit() {
    gcInitDesigns(); // before amounts: decides which pills are visible
    gcInitAmounts();
    gcInitCustomAmount();
    gcInitDelivery();
    gcSelectDelivery('email'); // digital-only: open recipient form on load
    gcInitSendTiming();
    gcInitGiftMessage();
    gcInitAccordions();
    gcInitFieldSync();
    gcInitApplePay();
    gcInitSticky();
    gcInitDateMin();
    gcInitShare();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', gcInit);
  } else {
    gcInit();
  }

})();
